import { useMemo, type ReactNode } from 'react';
import { Box, ThemeProvider, useTheme } from '@mui/material';
import { CONTENT_FONT_FAMILY_VAR } from '../constants/fonts';
import { createContentTheme } from './createContentTheme';

interface ContentThemeProviderProps {
  children: ReactNode;
  /**
   * Paint the full-bleed content canvas over the shell tile (default). Pass
   * `false` for pages rendered outside the shell (login), which lay out their
   * own canvas.
   */
  surface?: boolean;
}

/**
 * Applies the admin content theme (red accent, slate / #F1F5F9 canvas, Readex
 * Pro type scale, flat surfaces, operational status tones) to everything
 * rendered inside the shell tile.
 *
 * Mode and direction are read from the shell theme so the dark-mode toggle
 * keeps working; the shell itself (sidebar, app bar, account menu) never sees
 * this theme. Admin UI copy is English, so headings always use Latin tracking.
 * Deliberately no `CssBaseline` here: it would restyle `<body>` and leak the
 * content font into the shell.
 */
export function ContentThemeProvider({ children, surface = true }: ContentThemeProviderProps) {
  const shellTheme = useTheme();
  const mode = shellTheme.palette.mode;
  const direction = shellTheme.direction;

  const theme = useMemo(() => createContentTheme(mode, direction, { latin: true }), [mode, direction]);

  return <ThemeProvider theme={theme}>{surface ? <ContentSurface>{children}</ContentSurface> : children}</ThemeProvider>;
}

/**
 * Full-bleed canvas painted over the shell tile (which keeps its own 24px
 * padding and 16px corners). No transform / filter / contain here: fixed-
 * position overlays (maps, print roots) rely on the normal containing block.
 */
function ContentSurface({ children }: { children: ReactNode }) {
  return (
    <Box
      className="admin-content-surface"
      sx={{
        m: -3,
        px: { xs: 2, sm: 3, lg: 4 },
        py: { xs: 2.5, sm: 3, lg: 4 },
        borderRadius: '16px',
        bgcolor: 'background.default',
        color: 'text.primary',
        fontFamily: CONTENT_FONT_FAMILY_VAR,
        minHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 32px)' },
        transition: 'background-color 200ms ease',
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto', minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
